// SNS·메신저 인앱 브라우저(웹뷰) 판별과 외부 브라우저로 여는 주소 만들기(c014, 대표님 2026-10-22 "sns에서 열리는 브라우저 … 최적화").
// 웹뷰는 전체 화면·가로 고정을 못 하고 소리·저장이 불안정한 곳이 많아, 가능한 앱은 외부 브라우저로 넘기고 안 되면 안내 띠만 띄운다.
// 넘기지 못해도 게임은 웹뷰 안에서 그대로 돌아야 한다(넘기기는 편의이지 차단이 아니다). DOM을 만지지 않는 순수 모듈이다.

export type InAppApp = 'kakaotalk' | 'line' | 'instagram' | 'facebook' | 'naver' | 'band' | 'daum' | 'everytime' | 'tiktok' | 'webview';
export type MobileOs = 'android' | 'ios' | 'other';

export interface InAppInfo {
  /** 인앱 브라우저면 앱 종류, 실제 브라우저(Chrome·Safari·삼성 인터넷·웨일 등)면 null */
  app: InAppApp | null;
  os: MobileOs;
}

export interface DetectOpts {
  /** navigator.maxTouchPoints(iPadOS 13+ 데스크톱 모드 UA "Macintosh"를 iPad로 본다) */
  maxTouchPoints?: number;
  /** iOS 홈 화면 웹앱(navigator.standalone): Safari 토큰이 없어도 웹뷰가 아니다 */
  standalone?: boolean;
}

/** 이름이 분명한 앱(판별 순서가 중요하다: 네이버 앱 UA에도 "; wv)"가 있다) */
const APPS: readonly [InAppApp, RegExp][] = [
  ['kakaotalk', /KAKAOTALK/i],
  ['line', /\bLine\/\d/i],
  ['instagram', /Instagram/i],
  ['facebook', /FBAN\/|FBAV\/|FB_IAB|FBIOS|FB4A/],
  ['naver', /NAVER\(inapp/i],
  ['band', /\bBAND\//],
  ['daum', /DaumApps/i],
  ['everytime', /everytimeApp/i],
  ['tiktok', /musical_ly|BytedanceWebview|TikTok/i],
];

export function detectOs(ua: string, maxTouchPoints = 0): MobileOs {
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Macintosh/i.test(ua) && maxTouchPoints > 1) return 'ios';
  return 'other';
}

export function detectInApp(ua: string, opts: DetectOpts = {}): InAppInfo {
  const os = detectOs(ua, opts.maxTouchPoints ?? 0);
  for (const [app, re] of APPS) if (re.test(ua)) return { app, os };
  // 이름 모를 안드로이드 웹뷰: Android System WebView UA에는 "; wv)"가 붙는다(Chrome·Custom Tabs·삼성 인터넷에는 없다)
  if (os === 'android' && /;\s*wv\)/i.test(ua)) return { app: 'webview', os };
  // 이름 모를 iOS 웹뷰: WKWebView UA에는 "Safari/" 토큰이 없다(Safari·Chrome iOS·Firefox iOS·Edge iOS에는 있다). 홈 화면 웹앱은 뺀다
  if (os === 'ios' && !opts.standalone && /AppleWebKit/i.test(ua) && !/Safari\//i.test(ua)) return { app: 'webview', os };
  return { app: null, os };
}

/** 카카오톡: 외부 브라우저로 열기(안드로이드·iOS 모두 카카오톡이 지원하는 앱 스킴) */
export function kakaoExternalUrl(href: string): string {
  return 'kakaotalk://web/openExternal?url=' + encodeURIComponent(href);
}

/** 라인: openExternalBrowser=1을 붙이면 라인이 외부 브라우저로 연다. 이미 붙어 있으면 null(되풀이 방지) */
export function lineExternalUrl(href: string): string | null {
  const u = new URL(href);
  if (u.searchParams.get('openExternalBrowser') === '1') return null;
  u.searchParams.set('openExternalBrowser', '1');
  return u.toString();
}

/**
 * 안드로이드: Chrome으로 여는 인텐트 주소. Chrome이 없으면 browser_fallback_url(같은 주소)을 연다.
 * 조각(#…)은 버린다(c015): 인텐트 주소에서 '#'은 '#Intent;' 구분자라 조각을 실을 자리가 없고, 이전처럼 %23으로 바꿔 넣으면 Chrome이
 * /path%23frag(없는 경로)로 갔다. 게임은 조각을 쓰지 않는다(시작 문맥은 ?start= 쿼리). 대체 주소(fallback)도 조각 없는 같은 주소
 */
export function chromeIntentUrl(href: string): string {
  const u = new URL(href);
  u.hash = '';
  const scheme = u.protocol.replace(':', '') || 'https';
  return 'intent://' + u.host + u.pathname + u.search +
    '#Intent;scheme=' + scheme + ';package=com.android.chrome;S.browser_fallback_url=' + encodeURIComponent(u.toString()) + ';end';
}

export type EscapePlan =
  /** 불러올 때 바로 넘긴다(카카오톡·라인) */
  | { kind: 'auto'; url: string }
  /** 첫 탭에서 한 번 넘겨 본다(안드로이드 다른 웹뷰 → Chrome) */
  | { kind: 'firstTap'; url: string }
  | { kind: 'none' };

/**
 * 외부 브라우저로 넘기는 계획. tried=이번 세션에 이미 시도함(되풀이 방지). 실제 브라우저·PC·?inapp=0이면 none.
 * 라인 iOS도 openExternalBrowser를 지원한다. 그 밖의 iOS 웹뷰는 앱 밖으로 내보낼 방법이 없어 안내 띠만 쓴다.
 */
export function escapePlan(info: InAppInfo, href: string, tried: boolean): EscapePlan {
  if (!info.app || info.os === 'other' || tried) return { kind: 'none' };
  if (info.app === 'kakaotalk') return { kind: 'auto', url: kakaoExternalUrl(href) };
  if (info.app === 'line') {
    const url = lineExternalUrl(href);
    return url ? { kind: 'auto', url } : { kind: 'none' };
  }
  if (info.os === 'android') return { kind: 'firstTap', url: chromeIntentUrl(href) };
  return { kind: 'none' };
}

export type BannerKind = 'chrome' | 'kakao' | 'ios-menu' | null;

/** 안내 띠 종류: 안드로이드 = Chrome으로 열기, 카카오톡 = 외부 브라우저로 열기, iOS = 메뉴 → Safari 안내와 링크 복사 */
export function bannerKind(info: InAppInfo): BannerKind {
  if (!info.app || info.os === 'other') return null;
  if (info.app === 'kakaotalk') return 'kakao';
  return info.os === 'android' ? 'chrome' : 'ios-menu';
}

/** 앱 이름(안내 문구용) */
export function appLabel(app: InAppApp): string {
  switch (app) {
    case 'kakaotalk': return '카카오톡';
    case 'line': return '라인';
    case 'instagram': return '인스타그램';
    case 'facebook': return '페이스북';
    case 'naver': return '네이버';
    case 'band': return '밴드';
    case 'daum': return '다음';
    case 'everytime': return '에브리타임';
    case 'tiktok': return '틱톡';
    default: return '앱';
  }
}
